/**
 * This is the component that renders the chapter and subchapter rows in the Table of Contents.
 * It is used by the TableOfContents component.
 * @param {IChapter} chapter - The chapter data to be rendered
 * @param {boolean} [isFirst] - Whether the chapter is the first one in the list
 * @returns {React.ReactElement} A React component that displays a chapter and its subchapters
 */

import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { getColorObjectStyleByRecord } from 'PDF/helpers/documentStyle';

import { ChapterRow, ISubchapterItem } from './ChapterRow';

const colorIndexes = {
  green: 7,
  blue: 1,
  red: 1,
  blueGreen: 1,
  blueRed: 0,
  blueYellow: 1,
};

interface IChapter {
  chapter: {
    chapterNumber: string;
    chapterName: string;
    subChapters: ISubchapterItem[];
  };
  isFirst?: boolean;
}

export const Chapter = ({ chapter }: IChapter) => (
  <View style={styles.wrap}>
    <View wrap={false}>
      <Text style={styles.chapterNumber}>
        {chapter.chapterNumber.length === 1 ? `0${chapter.chapterNumber}` : chapter.chapterNumber}
      </Text>
      <View style={[{ borderBottom: !chapter.subChapters ? '0.8px' : '0px' }, styles.chapterRow]}>
        <Text style={[styles.chapterHeading, getColorObjectStyleByRecord(colorIndexes, 'color')]}>
          {chapter.chapterName}
        </Text>
      </View>
      {chapter.subChapters?.length > 0 && (
        <ChapterRow key={chapter.subChapters[0].id} data={chapter.subChapters[0]} isFirst />
      )}
    </View>
    {chapter.subChapters?.length > 0 &&
      chapter.subChapters.slice(1).map((item: any) => <ChapterRow key={item.id} data={item} />)}
  </View>
);

const styles = StyleSheet.create({
  wrap: {
    marginBottom: 19,
  },
  chapterNumber: {
    fontFamily: 'Noto',
    fontWeight: 600,
    fontSize: 21,
    color: '#212121',
  },
  chapterRow: {
    borderBottomStyle: 'dotted',
    borderColor: '#77787C',
    display: 'flex',
    flexDirection: 'row',
  },
  chapterHeading: {
    fontFamily: 'Noto',
    fontWeight: 600,
    fontSize: 12,
    lineHeight: 1.35,
    marginBottom: '6px',
  },
  pageNumber: {
    fontFamily: 'Noto',
    fontSize: 12,
    fontWeight: 600,
    marginLeft: 'auto',
  },
});
